// Generated automatically with "fut". Do not edit.

/**
 * Single-purpose class for converting Nintendo Mii formats to "Mii Studio data",
 * usable with the studio.mii.nintendo.com rendering API to get a Mii image.
 */
export class MiiDecoder {
    /**
   * @private
   */
    constructor() {
    }

    /**
     * Size of Wii format Mii data (RFLCharData).
   * @public
   * @readonly
   * @type {number}
   */
    static SIZE_WII_DATA = 74;

    /**
     * Base size of 3DS/Wii U format Mii data.
     * Usually 96 bytes long (FFLStoreData, Ver3StoreData).
   * @public
   * @readonly
   * @type {number}
   */
    static SIZE3DS_WIIU_DATA = 72;

    /**
     * Size of Switch "nn::mii::CharInfo" format.
   * @public
   * @readonly
   * @type {number}
   */
    static SIZE_NX_CHAR_INFO = 88;

    /**
   * @public
   * @readonly
   * @type {number}
   */
    static SIZE_STUDIO_RAW_DATA = 46;

    /**
   * @public
   * @readonly
   * @type {number}
   */
    static SIZE_STUDIO_URL_DATA = 47;

    /**
   * @public
   * @readonly
   * @type {number}
   */
    static SIZE_NX_STORE_DATA = 68;

    /**
   * @public
   * @readonly
   * @type {number}
   */
    static SIZE_NX_CORE_DATA = 48;

    /**
     * Base URL of /miis/image.png API that returns a Mii image.
     * This can also be replaced by mii-unsecure.ariankordi.net.
   * @public
   * @readonly
   * @type {string}
   */
    static URL_BASE_IMAGE_PNG = "https://truenas.local:8443/miis/image.png?shaderType=miitomo&data=";

    /**
     * Gets the Mii image URL using the Mii data converted in this instance.
     *
     * <p>Decodes the input Mii data, where the type is automatically detected.
     * Returns null if the format is not supported.
     * Formats (3DS/Wii U):
     * <ul>
     * <li>96 byte FFLStoreData/nn::mii::Ver3StoreData (has CRC)</li>
     * <li>92 byte FFLiMiiDataOfficial (in database, or kazuki-4ys ".3dsmii")</li>
     * <li>72 byte FFLiMiiDataCore (no creator name or CRC)
     * Formats (Wii):</li>
     * <li>76 byte RFLStoreData (has CRC)</li>
     * <li>74 byte RFLCharData (no CRC)
     * Formats (Switch, Studio):</li>
     * <li>88 byte nn::mii::CharInfo</li>
     * <li>47 byte obfuscated studio URL data</li>
     * <li>46 byte un-obfuscated raw "Studio Code"</li>
     * </ul>
   * @public
   * @param {Uint8Array} dst
   * @param {number} dataSize Size of the input data used to detect the type.
   * @param {Uint8Array} src Input Mii data to be converted.
   */
    static fromAnyMiiData(dst, dataSize, src) {
        switch (dataSize) {
            case 96:
            case 92:
            case 72:
                MiiDecoder.from3dsWiiuData(dst, src);
                return true;
            case 76:
            case 74:
                MiiDecoder.fromWiiData(dst, src);
                return true;
            case 46:
                dst.set(src.subarray(0, 46));
                return true;
            case 47:
                MiiDecoder._deobfuscateStudioUrl(dst, src);
                return true;
            case 88:
                MiiDecoder.fromNxCharInfo(dst, src);
                return true;
            case 68:
            case 48:
                MiiDecoder.fromNxCoreData(dst, src);
                return true;
            default:
                return false;
        }
    }

    /**
   * @public
   * @param {Uint8Array} dst
   * @param {MiiExtraInfo} dstExtra
   * @param {number} dataSize
   * @param {Uint8Array} src
   */
    static fromAnyMiiDataExtra(dst, dstExtra, dataSize, src) {
        switch (dataSize) {
            case 96:
            case 92:
            case 72:
                MiiDecoder.from3dsWiiuData(dst, src);
                MiiDecoder.setExtraFromStudioData(dstExtra, dst);
                MiiDecoder.setExtraFrom3dsWiiuData(dstExtra, src);
                return true;
            case 76:
            case 74:
                MiiDecoder.fromWiiData(dst, src);
                MiiDecoder.setExtraFromStudioData(dstExtra, dst);
                MiiDecoder.setExtraFromWiiData(dstExtra, src);
                return true;
            case 46:
                dst.set(src.subarray(0, 46));
                MiiDecoder.setExtraFromStudioData(dstExtra, dst);
                return true;
            case 47:
                MiiDecoder._deobfuscateStudioUrl(dst, src);
                MiiDecoder.setExtraFromStudioData(dstExtra, dst);
                return true;
            case 88:
                MiiDecoder.fromNxCharInfo(dst, src);
                MiiDecoder.setExtraFromStudioData(dstExtra, dst);
                MiiDecoder.setExtraFromNxCharInfo(dstExtra, src);
                return true;
            case 68:
            case 48:
                MiiDecoder.fromNxCoreData(dst, src);
                MiiDecoder.setExtraFromStudioData(dstExtra, dst);
                MiiDecoder.setExtraFromNxCoreData(dstExtra, src);
                return true;
            default:
                return false;
        }
    }

    /**
     * Verifies if the converted data in this instance is considered valid.
     * Derived from nn::mii::detail::CharInfoRaw::IsValid,
     * but does not return the specific reason.
   * @public
   * @param {Uint8Array} buf
   */
    static isValid(buf) {
        return buf[0] < 100 && buf[1] < 6 && 128 > buf[2] && buf[3] < 7 && buf[4] < 100 && buf[5] < 8 && buf[6] < 8 && buf[7] < 60 && buf[8] < 13 && buf[9] < 19 && buf[10] < 7 && buf[11] < 100 && buf[12] < 12 && buf[13] < 9 && buf[14] < 24 && buf[15] < 13 && buf[16] >= 3 && buf[16] <= 18 && buf[17] < 10 && buf[18] < 12 && buf[19] < 12 && buf[20] < 12 && buf[21] < 12 && buf[22] < 2 && buf[23] < 100 && buf[24] < 8 && buf[25] < 20 && buf[26] < 21 && buf[27] < 100 && buf[28] < 2 && buf[29] < 132 && 128 > buf[30] && buf[31] < 9 && buf[32] < 2 && buf[33] < 17 && buf[34] < 31 && buf[35] < 7 && buf[36] < 100 && buf[37] < 9 && buf[38] < 36 && buf[39] < 19 && buf[40] < 9 && buf[41] < 6 && buf[42] < 17 && buf[43] < 9 && buf[44] < 18 && buf[45] < 19;
    }

    /**
     * Decodes the input 3DS/Wii U Mii data (FFLStoreData/nn::mii::Ver3StoreData).
   * @public
   * @param {Uint8Array} dst
   * @param {Uint8Array} src
   */
    static from3dsWiiuData(dst, src) {
        dst[0] = src[66] >> 3 & 7;
        dst[1] = src[66] & 7;
        dst[2] = src[47];
        dst[3] = src[53] >> 5;
        dst[4] = (src[53] & 1) << 2 | src[52] >> 6;
        dst[5] = src[54] & 31;
        dst[6] = src[53] >> 1 & 15;
        dst[7] = src[52] & 63;
        dst[8] = (src[55] & 1) << 3 | src[54] >> 5;
        dst[9] = src[55] >> 1 & 31;
        dst[10] = src[57] >> 4 & 7;
        dst[11] = src[56] >> 5;
        dst[12] = src[58] & 31;
        dst[13] = src[57] & 15;
        dst[14] = src[56] & 31;
        dst[15] = (src[59] & 1) << 3 | src[58] >> 5;
        dst[16] = src[59] >> 1 & 31;
        dst[17] = src[48] >> 5;
        dst[18] = src[49] >> 4;
        dst[19] = src[48] >> 1 & 15;
        dst[20] = src[49] & 15;
        dst[21] = src[25] >> 2 & 15;
        dst[22] = src[24] & 1;
        dst[23] = src[68] >> 4 & 7;
        dst[24] = (src[69] & 7) * 2 | src[68] >> 7;
        dst[25] = src[68] & 15;
        dst[26] = src[69] >> 3;
        dst[27] = src[51] & 7;
        dst[28] = src[51] >> 3 & 1;
        dst[29] = src[50];
        dst[30] = src[46];
        dst[31] = src[70] >> 1 & 15;
        dst[32] = src[70] & 1;
        dst[33] = (src[71] & 3) << 3 | src[70] >> 5;
        dst[34] = src[71] >> 2 & 31;
        dst[35] = src[63] >> 5;
        dst[36] = (src[63] & 1) << 2 | src[62] >> 6;
        dst[37] = src[63] >> 1 & 15;
        dst[38] = src[62] & 63;
        dst[39] = src[64] & 31;
        dst[40] = (src[67] & 3) << 2 | src[66] >> 6;
        dst[41] = src[64] >> 5;
        dst[42] = src[67] >> 2 & 31;
        dst[43] = (src[61] & 1) << 3 | src[60] >> 5;
        dst[44] = src[60] & 31;
        dst[45] = src[61] >> 1 & 31;
        MiiDecoder._convertFieldsVer3ToNx(dst);
        return dst;
    }

    /**
     * Decodes the input Wii Mii data (RFLCharData, RFLStoreData).
   * @public
   * @param {Uint8Array} dst
   * @param {Uint8Array} src
   */
    static fromWiiData(dst, src) {
        dst[0] = src[50] >> 1 & 7;
        dst[1] = src[50] >> 4 & 3;
        dst[2] = src[23];
        dst[3] = 3;
        dst[4] = src[42] >> 5;
        dst[5] = src[41] >> 5 | (src[40] & 3) << 3;
        dst[6] = src[42] >> 1 & 15;
        dst[7] = src[40] >> 2;
        dst[8] = src[43] >> 5 | (src[42] & 1) << 3;
        dst[9] = src[41] & 31;
        dst[10] = 3;
        dst[11] = src[38] >> 5;
        dst[12] = src[37] >> 6 | (src[36] & 7) << 2;
        dst[13] = src[38] >> 1 & 15;
        dst[14] = src[36] >> 3;
        dst[15] = src[39] & 15;
        dst[16] = src[39] >> 4 | (src[38] & 1) << 4;
        dst[17] = src[32] >> 2 & 7;
        let faceTex = src[33] >> 6 | (src[32] & 3) << 2;
        dst[18] = MiiDecoder._FROM_WII_DATA_FACE_TEX_TABLE[faceTex * 2 + 1];
        dst[19] = src[32] >> 5;
        dst[20] = MiiDecoder._FROM_WII_DATA_FACE_TEX_TABLE[faceTex * 2];
        dst[21] = src[1] >> 1 & 15;
        dst[22] = src[0] >> 6 & 1;
        dst[23] = src[48] >> 1 & 7;
        dst[24] = src[49] >> 5 | (src[48] & 1) << 3;
        dst[25] = src[48] >> 4;
        dst[26] = src[49] & 31;
        dst[27] = src[35] >> 6 | (src[34] & 1) << 2;
        dst[28] = src[35] >> 5 & 1;
        dst[29] = src[34] >> 1;
        dst[30] = src[22];
        dst[31] = src[52] >> 3 & 15;
        dst[32] = src[52] >> 7;
        dst[33] = src[53] >> 1 & 31;
        dst[34] = src[53] >> 6 | (src[52] & 7) << 2;
        dst[35] = 3;
        dst[36] = src[46] >> 1 & 3;
        dst[37] = src[47] >> 5 | (src[46] & 1) << 3;
        dst[38] = src[46] >> 3;
        dst[39] = src[47] & 31;
        dst[40] = src[51] >> 5 | (src[50] & 1) << 3;
        dst[41] = src[50] >> 6;
        dst[42] = src[51] & 31;
        dst[43] = src[44] & 15;
        dst[44] = src[44] >> 4;
        dst[45] = src[45] >> 3;
        MiiDecoder._convertFieldsVer3ToNx(dst);
        return dst;
    }

    /**
     * Decodes the input Switch nn::mii::CharInfo format.
   * @public
   * @param {Uint8Array} dst
   * @param {Uint8Array} src
   */
    static fromNxCharInfo(dst, src) {
        dst[0] = src[74];
        dst[1] = src[75];
        dst[2] = src[42];
        dst[3] = src[55];
        dst[4] = src[53];
        dst[5] = src[56];
        dst[6] = src[54];
        dst[7] = src[52];
        dst[8] = src[57];
        dst[9] = src[58];
        dst[10] = src[62];
        dst[11] = src[60];
        dst[12] = src[63];
        dst[13] = src[61];
        dst[14] = src[59];
        dst[15] = src[64];
        dst[16] = src[65];
        dst[17] = src[46];
        dst[18] = src[48];
        dst[19] = src[45];
        dst[20] = src[47];
        dst[21] = src[39];
        dst[22] = src[40];
        dst[23] = src[80];
        dst[24] = src[81];
        dst[25] = src[79];
        dst[26] = src[82];
        dst[27] = src[50];
        dst[28] = src[51];
        dst[29] = src[49];
        dst[30] = src[41];
        dst[31] = src[84];
        dst[32] = src[83];
        dst[33] = src[85];
        dst[34] = src[86];
        dst[35] = src[72];
        dst[36] = src[70];
        dst[37] = src[71];
        dst[38] = src[69];
        dst[39] = src[73];
        dst[40] = src[77];
        dst[41] = src[76];
        dst[42] = src[78];
        dst[43] = src[67];
        dst[44] = src[66];
        dst[45] = src[68];
        return dst;
    }

    /**
     * Decodes the input Switch nn::mii::CoreData (database) format.
   * @public
   * @param {Uint8Array} dst
   * @param {Uint8Array} src
   */
    static fromNxCoreData(dst, src) {
        dst[0] = src[7] & 127;
        dst[1] = src[13] >> 5;
        dst[2] = src[2] & 127;
        dst[3] = src[17] >> 5;
        dst[4] = src[4] & 127;
        dst[5] = src[16] >> 5;
        dst[6] = src[18] >> 5;
        dst[7] = src[9] & 63;
        dst[8] = src[23] >> 4;
        dst[9] = src[11] & 31;
        dst[10] = src[15] >> 5;
        dst[11] = src[5] & 127;
        dst[12] = src[24] >> 4;
        dst[13] = src[24] & 15;
        dst[14] = src[12] & 31;
        dst[15] = src[25] & 15;
        dst[16] = (src[25] >> 4) + 3;
        dst[17] = src[22] & 15;
        dst[18] = src[23] & 15;
        dst[19] = src[21] >> 4;
        dst[20] = src[22] >> 4;
        dst[21] = src[21] & 15;
        dst[22] = src[4] >> 7;
        dst[23] = src[8] & 127;
        dst[24] = src[11] >> 5;
        dst[25] = src[20] & 31;
        dst[26] = src[17] & 31;
        dst[27] = src[3] & 127;
        dst[28] = src[2] >> 7;
        dst[29] = src[0];
        dst[30] = src[1] & 127;
        dst[31] = src[27] >> 4;
        dst[32] = src[1] >> 7;
        dst[33] = src[18] & 31;
        dst[34] = src[19] & 31;
        dst[35] = src[14] >> 5;
        dst[36] = src[6] & 127;
        dst[37] = src[26] >> 4;
        dst[38] = src[10] & 63;
        dst[39] = src[15] & 31;
        dst[40] = src[27] & 15;
        dst[41] = src[12] >> 5;
        dst[42] = src[16] & 31;
        dst[43] = src[26] & 15;
        dst[44] = src[13] & 31;
        dst[45] = src[14] & 31;
        return dst;
    }

    /**
     * Common method to convert colors and other fields from
     * 3DS/Wii U and Wii data to Switch/Studio equivalents.
   * @param {Uint8Array} buf
   */
    static _convertFieldsVer3ToNx(buf) {
        if (buf[27] == 0)
            buf[27] = 8;
        if (buf[0] == 0)
            buf[0] = 8;
        if (buf[11] == 0)
            buf[11] = 8;
        buf[36] += 19;
        buf[4] += 8;
        if (buf[23] == 0)
            buf[23] = 8;
        else if (buf[23] < 6)
            buf[23] += 13;
        if (127 < buf[2])
            buf[2] = 127;
        if (127 < buf[30])
            buf[30] = 127;
    }

    /**
     * Obfuscates Studio data to be used in the URL.
   * @public
   * @param {Uint8Array} dst
   * @param {Uint8Array} src
   * @param {number} [seed=0] The random value to use for the obfuscation. Best left as 0.
   */
    static obfuscateStudioUrl(dst, src, seed = 0) {
        dst[0] = seed;
        for (let i = 0; i < 46; i++) {
            let val = src[i] ^ dst[i];
            dst[i + 1] = (7 + val) % 256;
        }
    }

    /**
     * Deobfuscates Studio URL data to raw decodable data.
   * @param {Uint8Array} dst
   * @param {Uint8Array} src
   */
    static _deobfuscateStudioUrl(dst, src) {
        for (let i = 0; i < 46; i++) {
            let val = (src[i + 1] - 7) % 256;
            dst[i] = val ^ src[i];
        }
    }

    /**
   * @public
   * @param {MiiExtraInfo} dst
   * @param {Uint8Array} src
   */
    static setExtraFromStudioData(dst, src) {
        dst.isNx = true;
        dst.height = src[30];
        dst.build = src[2];
        dst.skinColor = src[17];
        dst.gender = src[22];
        dst.favoriteColor = src[21];
        dst.fontRegion = 0;
        dst.birthMonth = 0;
        dst.birthDay = 0;
        dst.favorite = false;
        dst.copyable = false;
        dst.ngWord = false;
        dst.localonly = false;
        dst.regionMove = 0;
        dst.nicknameSize = dst.creatorNameSize = 0;
    }

    /**
   * @public
   * @param {MiiExtraInfo} dst
   * @param {Uint8Array} src
   */
    static setExtraFrom3dsWiiuData(dst, src) {
        dst.isNx = false;
        dst.fontRegion = src[1] >> 4 & 3;
        dst.birthMonth = src[24] >> 1 & 15;
        dst.birthDay = (src[25] & 3) << 3 | src[24] >> 5;
        dst.favorite = (src[25] >> 6 & 1) == 1;
        dst.copyable = (src[1] & 1) == 1;
        dst.ngWord = (src[1] >> 1 & 1) == 1;
        dst.localonly = (src[48] & 1) == 1;
        dst.regionMove = src[1] >> 2 & 3;
        dst.authorId.set(src.subarray(4, 12));
        dst.createId.set(src.subarray(12, 22));
        const name = new Uint16Array(10);
        for (let i = 0; i < 10; i++)
            name[i] = src[26 + i * 2] | src[26 + i * 2 + 1] << 8;
        const creator = new Uint16Array(10);
        for (let i = 0; i < 10; i++)
            creator[i] = src[72 + i * 2] | src[72 + i * 2 + 1] << 8;
        dst.nicknameSize = MiiDecoder._char16ToUtf8(dst.nicknameData, name, 10);
        dst.creatorNameSize = MiiDecoder._char16ToUtf8(dst.creatorNameData, creator, 10);
    }

    /**
   * @public
   * @param {MiiExtraInfo} dst
   * @param {Uint8Array} src
   */
    static setExtraFromWiiData(dst, src) {
        dst.isNx = false;
        dst.fontRegion = 0;
        dst.birthMonth = src[0] >> 2 & 15;
        dst.birthDay = src[1] >> 5 | (src[0] & 3) << 3;
        dst.favorite = (src[1] & 1) == 1;
        dst.copyable = false;
        dst.ngWord = false;
        dst.localonly = (src[33] >> 2 & 1) == 1;
        dst.regionMove = 0;
        dst.createId.set(src.subarray(24, 32));
        const name = new Uint16Array(10);
        for (let i = 0; i < 10; i++)
            name[i] = src[2 + i * 2] << 8 | src[2 + i * 2 + 1];
        const creator = new Uint16Array(10);
        for (let i = 0; i < 10; i++)
            creator[i] = src[54 + i * 2] << 8 | src[54 + i * 2 + 1];
        dst.nicknameSize = MiiDecoder._char16ToUtf8(dst.nicknameData, name, 10);
        dst.creatorNameSize = MiiDecoder._char16ToUtf8(dst.creatorNameData, creator, 10);
    }

    /**
   * @public
   * @param {MiiExtraInfo} dst
   * @param {Uint8Array} src
   */
    static setExtraFromNxCharInfo(dst, src) {
        dst.isNx = true;
        dst.fontRegion = src[38];
        dst.createId.set(src.subarray(0, 16));
        const name = new Uint16Array(10);
        for (let i = 0; i < 10; i++)
            name[i] = src[16 + i * 2] | src[16 + i * 2 + 1] << 8;
        dst.nicknameSize = MiiDecoder._char16ToUtf8(dst.nicknameData, name, 10);
    }

    /**
   * @public
   * @param {MiiExtraInfo} dst
   * @param {Uint8Array} src
   */
    static setExtraFromNxCoreData(dst, src) {
        dst.isNx = true;
        dst.fontRegion = src[10] >> 6;
        const name = new Uint16Array(10);
        for (let i = 0; i < 10; i++)
            name[i] = src[28 + i * 2] | src[28 + i * 2 + 1] << 8;
        dst.nicknameSize = MiiDecoder._char16ToUtf8(dst.nicknameData, name, 10);
    }

    /**
     * Writes UTF-8 text from a buffer of 16-bit wide characters
     * that are within the Unicode BMP, also known as UCS-2 encoded text.
     * This does not support decoding UTF-16 surrogate pairs for
     * characters beyond the Basic Multilingual Plane (emoji, etc.)
   * @param {Uint8Array} dst Destination UTF-8 bytes to write to. Size MUST be 3 * characterCount.
   * @param {Uint16Array} src Source array of 16-bit code points.
   * @param {number} characterCount Amount of characters from the original string to process.
   */
    static _char16ToUtf8(dst, src, characterCount) {
        let iDst = 0;
        for (let i = 0; i < characterCount && src[i] != 0; i++) {
            let chr = src[i];
            if (chr <= 127)
                dst[iDst++] = chr;
            else if (chr <= 2047) {
                dst[iDst + 0] = 192 | chr >> 6;
                dst[iDst + 1] = 128 | (chr & 63);
                iDst += 2;
            }
            else {
                if (chr >= 55296 && chr <= 57343)
                    chr = 65533;
                dst[iDst + 0] = 224 | chr >> 12;
                dst[iDst + 1] = 128 | (chr >> 6 & 63);
                dst[iDst + 2] = 128 | (chr & 63);
                iDst += 3;
            }
        }
        return iDst;
    }

    /**
   * @param {Uint16Array} dst
   * @param {Uint8Array} src
   * @param {number} srcSize
   */
    static _utf8ToChar16(dst, src, srcSize) {
        let iDst = 0;
        for (let i = 0; i < srcSize && src[i] != 0; iDst++) {
            let chr = 0;
            if ((src[i] & 224) == 224 && (src[i] & 16) == 0) {
                chr = (src[i + 0] & 15) << 12 | (src[i + 1] & 63) << 6 | (src[i + 2] & 63) << 0;
                i += 3;
            }
            else if ((src[i] & 192) == 192 && (src[i] & 32) == 0) {
                chr = (src[i + 0] & 31) << 6 | (src[i + 1] & 63) << 0;
                i += 2;
            }
            else
                chr = src[i++] & 127;
            dst[iDst] = chr;
        }
        return iDst;
    }

    /**
   * @readonly
   * @type {Uint8Array}
   */
    static _FROM_WII_DATA_FACE_TEX_TABLE = new Uint8Array([0, 0, 0, 1, 0, 6, 0, 9, 5, 0, 2, 0, 3, 0, 7, 0,
        8, 0, 0, 10, 9, 0, 11, 0]);
}

/**
 * a.
 * TODO: where is birth platform?
 * also, ver3/rfl createid methods would be useful,
 * such as: special, temporary, platform, dates?
 */
export class MiiExtraInfo {

    /**
   * @readonly
   * @type {number}
   */
    static _NAME_LENGTH = 10;
    /**
     * True if the info is from NX Mii data.
   * @public
   * @type {boolean}
   */
    isNx;
    /**
     * Mii's nickname as UTF-8.
   * @public
   * @readonly
   * @type {Uint8Array}
   */
    nicknameData = new Uint8Array(30);
    /**
     * Mii's creator name as UTF-8.
     * Only present if IsNx = false.
   * @public
   * @readonly
   * @type {Uint8Array}
   */
    creatorNameData = new Uint8Array(30);
    /**
     * Size of buffer for nickname in bytes.
   * @public
   * @type {number}
   */
    nicknameSize;
    /**
     * Size of buffer for creator name in bytes.
   * @public
   * @type {number}
   */
    creatorNameSize;
    /**
   * @public
   * @type {number}
   */
    height;
    /**
   * @public
   * @type {number}
   */
    build;
    /**
   * @public
   * @type {number}
   */
    skinColor;
    /**
   * @public
   * @type {number}
   */
    gender;
    /**
   * @public
   * @type {number}
   */
    favoriteColor;
    /**
   * @public
   * @type {number}
   */
    fontRegion;
    /**
     * Only present if IsNx = false.
   * @public
   * @type {number}
   */
    birthMonth;
    /**
     * Only present if IsNx = false.
   * @public
   * @type {number}
   */
    birthDay;
    /**
     * Only present if IsNx = false.
   * @public
   * @type {boolean}
   */
    favorite;
    /**
     * Only present if IsNx = false.
   * @public
   * @type {boolean}
   */
    copyable;
    /**
     * Only present if IsNx = false.
   * @public
   * @type {boolean}
   */
    ngWord;
    /**
     * Only present if IsNx = false.
   * @public
   * @type {boolean}
   */
    localonly;
    /**
     * Only present if IsNx = false.
   * @public
   * @type {number}
   */
    regionMove;
    /**
     * Mii unique ID.
     * When IsNx = true, this is a 16-byte UUIDv4,
     * otherwise 10 bytes (3DS/Wii U) or 8 bytes (Wii).
   * @public
   * @readonly
   * @type {Uint8Array}
   */
    createId = new Uint8Array(16);
    /**
     * Mii console ID only for 3DS/Wii U data.
     * Only present if IsNx = false.
     * This is equal to the Transferable ID (not permanent).
   * @public
   * @readonly
   * @type {Uint8Array}
   */
    authorId = new Uint8Array(8);

    /**
   * @public
   */
    getNickname() {
        return new TextDecoder().decode(this.nicknameData.subarray(0, this.nicknameSize));
    }

    /**
   * @public
   */
    getCreatorName() {
        return new TextDecoder().decode(this.creatorNameData.subarray(0, this.creatorNameSize));
    }
}

/**
 * Utility class for decoding a hexadecimal string to a buffer.
 */
export class HexDecoder {
    /**
   * @private
   */
    constructor() {
    }

    /**
     * Gets the character code corresponding to the hexadecimal number.
   * @param {number} c
   */
    static _hexValue(c) {
        if (c >= 48 && c <= 57)
            return c - 48;
        if (c >= 65 && c <= 70)
            return c - 65 + 10;
        if (c >= 97 && c <= 102)
            return c - 97 + 10;
        return -1;
    }

    /**
     * Decodes a hexadecimal string into a pre-allocated buffer.
   * @public
   * @param {string} text
   * @param {Uint8Array} buf
   * @param {number} bufSize
   */
    static decode(text, buf, bufSize) {
        let len = text.length;
        if ((len & 1) != 0)
            return -1;
        let byteCount = len >> 1;
        if (byteCount > bufSize)
            return -1;
        for (let i = 0; i < byteCount; i++) {
            let hi = HexDecoder._hexValue(text.charCodeAt(i << 1));
            let lo = HexDecoder._hexValue(text.charCodeAt((i << 1) + 1));
            if (hi < 0 || lo < 0)
                return -1;
            buf[i] = (hi << 4 | lo) & 255;
        }
        return byteCount;
    }
}

export class Program {
    /**
   * @private
   */
    constructor() {
    }

    /**
   * @public
   * @param {readonly string[]} args
   */
    static main(args) {
        const studio = new Uint8Array(47);
        const buffer = new Uint8Array(127);
        if (args.length < 1) {
            console.error("Usage: ./MiiToStudio <Mii data hex>");
            console.error("  Supported sizes (auto-detected): 46, 47, 74, 76, 88, 92, 96 bytes");
            console.error("  Linux/macOS: ./MiiToStudio \"$(xxd -p -c0 MyMii.ffsd)\"");
            console.error("  PowerShell: .\\MiiToStudio (([System.IO.File]::ReadAllBytes(\"MyMii.ffsd\") | ForEach-Object { $_.ToString(\"x2\") }) -join \"\")");
            return 1;
        }
        let size = HexDecoder.decode(args[0], buffer, 127);
        if (size < 0) {
            console.error("Invalid hexadecimal input.");
            return 1;
        }
        const extra = new MiiExtraInfo();
        let success = MiiDecoder.fromAnyMiiDataExtra(studio, extra, size, buffer);
        if (!success) {
            console.error("MiiToStudio.FromAnyMiiData() could not determine input type.");
            return 1;
        }
        if (!MiiDecoder.isValid(studio)) {
            console.error("Mii data is invalid (MiiToStudio.IsValid() == false).");
            return 1;
        }
        console.log(`Name: ${extra.getNickname()}`);
        console.log(`Creator Name: ${extra.getCreatorName()}`);
        console.log(`Height: ${extra.height}`);
        console.log(`Build: ${extra.build}`);
        console.log(`Gender: ${extra.gender}`);
        console.log(`Favorite Color ID: ${extra.favoriteColor}`);
        console.log(`Birth Month: ${extra.birthMonth}`);
        console.log(`Birth Day: ${extra.birthDay}`);
        return 0;
    }
}

// process.exit(Program.main(process.argv.slice(2)));
